import * as React from "react";
import {useTranslation} from "react-i18next";
import {useAuth} from "../context/AuthContext";
import {usePeriod} from "../context/PeriodContext";
import {hasRole} from "../utils/jwtHelper";
import type {AllPayrollPeriod} from "../services/payrollService";
import {getAllPayrollPeriods} from "../services/payrollService";
import {API_BASE_URL} from "../config/apiConfig";
import {
    getComparator,
    type Order,
    type SortableKey,
    type WorkEvidenceRecord,
} from "../components/WorkEvidence/WorkEvidenceApprovalTableComponent";
import {isApprovedStatus, isRejectedStatus, isSubmittedStatus} from "../components/WorkEvidence/StatusChip";

export type ApprovalStatusFilter = "PENDING" | "APPROVED";

// Type for branch response
interface Branch {
    id: string;
    name: string;
}

interface BranchesResponse {
    totalRecords: number;
    tenants: Branch[];
}

interface WorkEvidenceResponse {
    totalRecords: number;
    workEvidences: WorkEvidenceRecord[];
}

export function useWorkEvidenceApproval() {
    const {t} = useTranslation();
    const [workEvidenceData, setWorkEvidenceData] = React.useState<WorkEvidenceRecord[]>([]);
    const [isLoadingData, setIsLoadingData] = React.useState<boolean>(false);
    const [tableDataError, setTableDataError] = React.useState<string | null>(null);
    const [currentPage, setCurrentPage] = React.useState<number>(1);
    const itemsPerPage = 5;

    // Sorting state (must be after workEvidenceData is declared)
    const [order, setOrder] = React.useState<Order>("asc");
    const [orderBy, setOrderBy] = React.useState<SortableKey>("firstName");

    const handleRequestSort = (property: SortableKey) => {
        const isAsc = orderBy === property && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
    };

    // Sort data before paginating (must be at top level for hooks rule)
    const sortableData = React.useMemo(
        () => [...workEvidenceData].sort(getComparator(order, orderBy)),
        [workEvidenceData, order, orderBy],
    );
    const {
        selectedPeriod,
        selectedPeriodValue,
        dashboardBranch,
        dashboardBranchName,
    } = usePeriod();
    // Mirrors workEvidence.ts/usePayrollRun.ts: the top toolbar's branch selector (and its mobile
    // equivalent) write to `dashboardBranch`/`dashboardBranchName`, not `selectedBranch` — see
    // PeriodContext for why the two are kept separate. This page has no local branch selector of
    // its own, so it must read the toolbar's value to actually reflect what the user picked there.
    const selectedBranch = dashboardBranch;
    const selectedBranchName = dashboardBranchName;
    const {token} = useAuth();

    // Role-based access checks
    const canViewPage = hasRole(token, "adm_work_evidence_approval");
    const canApprove = canViewPage;

    const [totalRecords, setTotalRecords] = React.useState<number>(0);
    const [allPeriods, setAllPeriods] = React.useState<AllPayrollPeriod[]>([]);
    const [branches, setBranches] = React.useState<Branch[]>([]);
    const [loadingBranches, setLoadingBranches] = React.useState<boolean>(false);
    const [branchesError, setBranchesError] = React.useState<string | null>(null);

    const [successMessage, setSuccessMessage] = React.useState<string | null>(null);
    // Derived directly from the (possibly toolbar-changed) selectedPeriodValue on every render,
    // rather than mirrored into its own state, so it can't go stale when the period changes.
    const isPeriodLocked = selectedPeriodValue?.toUpperCase().includes("LOCKED") ?? false;
    const isPeriodOpen = selectedPeriodValue?.toUpperCase().includes("OPEN") ?? false;
    const [approvingId, setApprovingId] = React.useState<string | null>(null);
    const [rejectingId, setRejectingId] = React.useState<string | null>(null);
    const [isBulkLoading, setIsBulkLoading] = React.useState<boolean>(false);

    // Confirmation dialog state
    const [confirmOpen, setConfirmOpen] = React.useState<boolean>(false);
    const [confirmMessage, setConfirmMessage] = React.useState<string>("");
    const [confirmPendingAction, setConfirmPendingAction] = React.useState<(() => void) | null>(null);

    // Row selection state
    const [selected, setSelected] = React.useState<string[]>([]);

    // Search / tab (pending vs approved) filtering — see WorkEvidenceApproval.tsx's desktop
    // reference layout (search field + "Në pritje"/"Të miratuara" tabs above the table).
    const [searchTerm, setSearchTerm] = React.useState<string>("");
    const [statusFilter, setStatusFilter] = React.useState<ApprovalStatusFilter>("PENDING");

    const stats = React.useMemo(() => {
        const total = workEvidenceData.length;
        const approved = workEvidenceData.filter((r) => isApprovedStatus(r.status)).length;
        const rejected = workEvidenceData.filter((r) => isRejectedStatus(r.status)).length;
        const submitted = workEvidenceData.filter((r) => isSubmittedStatus(r.status)).length;
        // "pending" = actionable here (submitted from Work Evidence); the rest hasn't been sent yet.
        return {total, approved, rejected, pending: submitted, notSent: total - approved - submitted};
    }, [workEvidenceData]);

    const filteredData = React.useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        return sortableData.filter((record) => {
            const matchesSearch =
                term.length === 0 ||
                `${record.firstName} ${record.lastName}`.toLowerCase().includes(term);
            const matchesStatus = statusFilter === "APPROVED"
                ? isApprovedStatus(record.status)
                : isSubmittedStatus(record.status);
            return matchesSearch && matchesStatus;
        });
    }, [sortableData, searchTerm, statusFilter]);

    // --- Checkbox selection handlers ---
    const isSelected = (id: string) => selected.includes(id);

    const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            setSelected(filteredData.map((r) => r.employeeId));
        } else {
            setSelected([]);
        }
    };

    const handleRowCheckboxClick = (event: React.MouseEvent, id: string) => {
        event.stopPropagation();
        setSelected((prev) =>
            prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id],
        );
    };

    // --- Confirmation dialog helpers ---
    const showConfirm = (message: string, onConfirm: () => void) => {
        setConfirmMessage(message);
        setConfirmPendingAction(() => onConfirm);
        setConfirmOpen(true);
    };

    const handleConfirmClose = () => {
        setConfirmOpen(false);
        setConfirmMessage("");
        setConfirmPendingAction(null);
        setApprovingId(null);
        setRejectingId(null);
    };

    const handleConfirmYes = () => {
        if (confirmPendingAction) {
            confirmPendingAction();
        }
        handleConfirmClose();
    };

    // --- Core API calls (PUT, ids array) ---
    const executeApprove = async (ids: string[]) => {
        if (!token || !selectedPeriod || !selectedBranch || ids.length === 0) return;
        setIsBulkLoading(true);
        setTableDataError(null);
        try {
            const response = await fetch(`${API_BASE_URL}/api/work-evidence/approve`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ids}),
            });

            if (!response.ok) {
                throw new Error(`Failed to approve work evidence: ${response.statusText}`);
            }

            setSuccessMessage(t('workEvidence.approval.success.approved', {count: ids.length}));
            setTimeout(() => setSuccessMessage(null), 3000);
            setSelected([]);
            await fetchWorkEvidenceData(selectedPeriod, selectedBranch);
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : t('workEvidence.approval.error.approveFailed');
            console.error("Error approving work evidence:", error);
            setTableDataError(errorMessage);
        } finally {
            setIsBulkLoading(false);
            setApprovingId(null);
        }
    };

    const executeReject = async (ids: string[]) => {
        if (!token || !selectedPeriod || !selectedBranch || ids.length === 0) return;
        setIsBulkLoading(true);
        setTableDataError(null);
        try {
            const response = await fetch(`${API_BASE_URL}/api/work-evidence/reject`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ids}),
            });

            if (!response.ok) {
                throw new Error(`Failed to reject work evidence: ${response.statusText}`);
            }

            setSuccessMessage(t('workEvidence.approval.success.rejected', {count: ids.length}));
            setTimeout(() => setSuccessMessage(null), 3000);
            setSelected([]);
            await fetchWorkEvidenceData(selectedPeriod, selectedBranch);
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : t('workEvidence.approval.error.rejectFailed');
            console.error("Error rejecting work evidence:", error);
            setTableDataError(errorMessage);
        } finally {
            setIsBulkLoading(false);
            setRejectingId(null);
        }
    };

    // --- Single-row wrappers (show confirmation first) ---
    const handleApprove = (record: WorkEvidenceRecord) => {
        if (!record.workEvidenceId) return;
        setApprovingId(record.employeeId);
        showConfirm(
            t('workEvidence.approval.confirm.approveOne', {firstName: record.firstName, lastName: record.lastName}),
            () => executeApprove([record.workEvidenceId as string]),
        );
    };

    const handleReject = (record: WorkEvidenceRecord) => {
        if (!record.workEvidenceId) return;
        setRejectingId(record.employeeId);
        showConfirm(
            t('workEvidence.approval.confirm.rejectOne', {firstName: record.firstName, lastName: record.lastName}),
            () => executeReject([record.workEvidenceId as string]),
        );
    };

    // --- Bulk wrappers ---
    const handleBulkApprove = () => {
        const ids = workEvidenceData
            .filter((r) => selected.includes(r.employeeId) && r.workEvidenceId)
            .map((r) => r.workEvidenceId as string);
        if (ids.length === 0) return;
        showConfirm(
            t('workEvidence.approval.confirm.approveBulk', {count: ids.length}),
            () => executeApprove(ids),
        );
    };

    const handleBulkReject = () => {
        const ids = workEvidenceData
            .filter((r) => selected.includes(r.employeeId) && r.workEvidenceId)
            .map((r) => r.workEvidenceId as string);
        if (ids.length === 0) return;
        showConfirm(
            t('workEvidence.approval.confirm.rejectBulk', {count: ids.length}),
            () => executeReject(ids),
        );
    };

    const fetchBranches = async () => {
        if (!token) return;

        try {
            setLoadingBranches(true);
            setBranchesError(null);

            const response = await fetch(`${API_BASE_URL}/api/branches/get`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch branches: ${response.statusText}`);
            }

            const data: BranchesResponse = await response.json();
            setBranches(data.tenants || []);
            setTotalRecords(data.totalRecords || 0);
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : t('hr.branches.error.fetchFailed');
            console.error("Error fetching branches:", error);
            setBranchesError(errorMessage);
            setBranches([]);
        } finally {
            setLoadingBranches(false);
        }
    };

    const fetchWorkEvidenceData = async (periodKey: string, branchId: string) => {
        if (!token) return;

        try {
            setIsLoadingData(true);
            setTableDataError(null);

            const response = await fetch(
                `${API_BASE_URL}/api/work-evidence/get?periodKey=${periodKey}&branch=${branchId}`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                },
            );

            if (!response.ok) {
                throw new Error(`Failed to fetch work evidence data: ${response.statusText}`);
            }

            const data: WorkEvidenceResponse = await response.json();
            setWorkEvidenceData(data.workEvidences || []);
            setCurrentPage(1);
        } catch (error) {
            const errorMessage =
                error instanceof Error ? error.message : t('workEvidence.error.fetchDataFailed');
            console.error("Error fetching work evidence data:", error);
            setTableDataError(errorMessage);
            setWorkEvidenceData([]);
        } finally {
            setIsLoadingData(false);
        }
    };


    React.useEffect(() => {
        const fetchAllPeriods = async () => {
            if (!token) return;
            try {
                const periodsResponse = await getAllPayrollPeriods(token);
                setAllPeriods(periodsResponse);
            } catch (error) {
                console.error("Failed to fetch all payroll periods:", error);
                setAllPeriods([]);
            }
        };
        fetchAllPeriods();
    }, [token]);

    React.useEffect(() => {
        fetchBranches();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    // Period/branch selection comes from the global toolbar selector (PeriodBranchToolbarSelector)
    // rather than a selector local to this page, so this effect must re-fetch whenever either
    // changes - not just once on mount (see workEvidence.ts for the same fix).
    React.useEffect(() => {
        if (token && selectedPeriod && selectedBranch) {
            setSelected([]);
            fetchWorkEvidenceData(selectedPeriod, selectedBranch);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, selectedPeriod, selectedBranch]);

    // Reset to page 1 whenever the visible dataset shrinks (search/tab change)
    React.useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, statusFilter]);

    return {
        canViewPage,
        canApprove,
        selectedPeriod,
        selectedPeriodValue,
        selectedBranch,
        selectedBranchName,
        allPeriods,
        branches,
        loadingBranches,
        branchesError,
        isPeriodOpen,
        isPeriodLocked,
        totalRecords,
        workEvidenceData,
        sortableData,
        searchTerm,
        setSearchTerm,
        statusFilter,
        setStatusFilter,
        stats,
        filteredData,
        currentPage,
        setCurrentPage,
        itemsPerPage,
        selected,
        approvingId,
        rejectingId,
        isBulkLoading,
        isSelected,
        handleSelectAllClick,
        handleRowCheckboxClick,
        handleApprove,
        handleReject,
        handleBulkApprove,
        handleBulkReject,
        order,
        orderBy,
        handleRequestSort,
        isLoadingData,
        tableDataError,
        confirmOpen,
        confirmMessage,
        handleConfirmClose,
        handleConfirmYes,
        successMessage,
        setSuccessMessage,
    };
}
